import { Clipboard } from '@angular/cdk/clipboard';
import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { TRACE_CONFIG } from './trace.config';
import { TraceService } from './trace.service';
import { TelemetryService } from './telemetry.service';

/**
 * Adds the error reference (backend x-trace-id, else the interceptor's trace
 * id) to error messages shown after failed API calls. With the trace flag off
 * every method leaves the message untouched.
 */
@Injectable({ providedIn: 'root' })
export class ErrorReferenceService {
  private readonly trace = inject(TraceService);
  private readonly telemetry = inject(TelemetryService);
  private readonly snackbar = inject(MatSnackBar);
  private readonly clipboard = inject(Clipboard);

  private readonly handled = new WeakSet<object>();
  private latest: string | null = null;

  /** Most recent reference shown to the user (for error pages). */
  get latestReference(): string | null {
    return this.latest;
  }

  /** Reference for the error, or null when the feature is off or no error was given. */
  referenceFor(error: unknown): string | null {
    if (!this.trace.isEnabled() || error === undefined || error === null) {
      return null;
    }
    const reference = this.resolve(error);
    this.latest = reference;
    return reference;
  }

  /**
   * Attaches a backend trace id (e.g. the traceId field of a status:false
   * response body) to an error object so the reference shown to the user is
   * the one the server logged, not a locally minted one.
   */
  recordBackendReference(error: object, traceId: string | null | undefined): void {
    if (traceId?.trim()) {
      this.trace.recordErrorReference(error, this.trace.toReference(traceId));
    }
  }

  private resolve(error: unknown): string {
    const unwrapped = this.unwrap(error);
    if (typeof unwrapped !== 'object' || unwrapped === null) {
      // Primitive errors cannot be keyed, so every call mints a new reference.
      const reference = this.trace.toReference(this.trace.createContext().traceId);
      this.telemetry.recordEvent('error_shown', { reference });
      return reference;
    }
    let reference = this.trace.getErrorReference(unwrapped);
    if (!reference) {
      reference = this.trace.toReference(this.trace.createContext().traceId);
      this.trace.recordErrorReference(unwrapped, reference);
    }
    if (!this.handled.has(unwrapped)) {
      // Once per error: the only record of a browser-minted reference is this RUM event,
      // so support can resolve "Reference: XXXX" even when no backend trace exists.
      this.telemetry.recordEvent('error_shown', { reference });
    }
    this.handled.add(unwrapped);
    return reference;
  }

  /** True once a component has shown this error to the user. */
  isHandled(error: unknown): boolean {
    const unwrapped = this.unwrap(error);
    return typeof unwrapped === 'object' && unwrapped !== null && this.handled.has(unwrapped);
  }

  formatMessage(message: string, error?: unknown): string {
    const reference = this.referenceFor(error);
    return reference ? `${message} Reference: ${reference}` : message;
  }

  openErrorSnackbar(
    message: string,
    error?: unknown,
    action = 'Close',
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<TextOnlySnackBar> {
    const reference = this.referenceFor(error);
    if (!reference) {
      return this.snackbar.open(message, action, { ...config, duration: TRACE_CONFIG.toastDurationMs });
    }
    return this.openReferenceSnackbar(message, reference, config);
  }

  openReferenceSnackbar(
    message: string,
    reference: string,
    config?: MatSnackBarConfig,
  ): MatSnackBarRef<TextOnlySnackBar> {
    const ref = this.snackbar.open(`${message} Reference: ${reference}`, 'Copy', {
      ...config,
      duration: TRACE_CONFIG.toastDurationMs,
    });
    ref.onAction().subscribe(() => this.clipboard.copy(reference));
    return ref;
  }

  copy(reference: string): void {
    this.clipboard.copy(reference);
  }

  // Zone wraps rejections as { rejection: error }.
  private unwrap(error: unknown): unknown {
    if (typeof error === 'object' && error !== null && 'rejection' in error) {
      return (error as { rejection: unknown }).rejection;
    }
    return error;
  }
}
